import React, { useState, useRef, useEffect } from 'react';
import { X, Check, RotateCcw } from 'lucide-react';
import './ClipTrimmerModal.css';

export const ClipTrimmerModal = ({ isOpen, onClose, clip, videoDuration, jobId, session, onSave }) => {
    // Buffer logic: show 10s before and 10s after the CURRENT clip bounds (or original bounds)
    // Actually, user wants "extra 10 seconds on each side".
    // So the viewable window should be [start - 10, end + 10].

    // We maintain the "window" of the timeline.
    // The "selection" is the actual clip start/end.

    const [start, setStart] = useState(clip?.start || 0);
    const [end, setEnd] = useState(clip?.end || 10);

    const [transcript, setTranscript] = useState(null);
    const [loadingTranscript, setLoadingTranscript] = useState(false);

    // 10s buffer constraints
    const minTime = Math.max(0, (clip?.start || 0) - 10);
    const maxTime = Math.min(videoDuration || 9999, (clip?.end || 0) + 10);
    const windowDuration = maxTime - minTime;

    const containerRef = useRef(null);
    const [isDragging, setIsDragging] = useState(null);

    // Fetch transcript on mount
    useEffect(() => {
        if (!jobId || !session?.access_token) return;

        const fetchTranscript = async () => {
            setLoadingTranscript(true);
            try {
                // Use API_BASE from props or env or hardcode consistent with app
                const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';
                const res = await fetch(`${API_BASE}/api/clip-intel/jobs/${jobId}/transcript`, {
                    headers: { 'Authorization': `Bearer ${session.access_token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    // Handle both raw array or { transcript: { words: [] } } format
                    const words = data.transcript?.words || data.words || [];
                    setTranscript(words);
                }
            } catch (err) {
                console.error("Failed to load transcript:", err);
            } finally {
                setLoadingTranscript(false);
            }
        };
        fetchTranscript();
    }, [jobId, session]);

    useEffect(() => {
        if (isOpen && clip) {
            setStart(clip.start);
            setEnd(clip.end);
        }
    }, [isOpen, clip]);

    const handleSave = () => {
        onSave(start, end);
        onClose();
    };

    // Timeline Drag Logic
    const handlePointerDown = (type) => (e) => {
        setIsDragging(type);
        e.preventDefault();
        e.stopPropagation();
    };

    useEffect(() => {
        const handleMove = (e) => {
            if (!isDragging || !containerRef.current) return;

            const rect = containerRef.current.getBoundingClientRect();
            const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
            const percentage = x / rect.width;

            // Map percentage to time within the window [minTime, maxTime]
            const time = minTime + (percentage * windowDuration);
            const snappedTime = Math.round(time * 10) / 10;

            if (isDragging === 'start') {
                const newStart = Math.min(snappedTime, end - 0.5);
                setStart(Math.max(minTime, newStart));
            } else if (isDragging === 'end') {
                const newEnd = Math.max(snappedTime, start + 0.5);
                setEnd(Math.min(maxTime, newEnd));
            }
        };

        const handleUp = () => setIsDragging(null);

        if (isDragging) {
            window.addEventListener('pointermove', handleMove);
            window.addEventListener('pointerup', handleUp);
            window.addEventListener('pointercancel', handleUp);
        }
        return () => {
            window.removeEventListener('pointermove', handleMove);
            window.removeEventListener('pointerup', handleUp);
            window.removeEventListener('pointercancel', handleUp);
        };
    }, [isDragging, start, end, minTime, maxTime, windowDuration]);

    // Words within visible window
    const visibleWords = transcript ? transcript.filter(w => w.end > minTime && w.start < maxTime) : [];

    if (!isOpen) return null;

    // Calculate percentages for visual display relative to the WINDOW
    const leftPct = ((start - minTime) / windowDuration) * 100;
    const rightPct = ((end - minTime) / windowDuration) * 100;
    const widthPct = rightPct - leftPct;

    return (
        <div className="clip-trimmer-modal-overlay">
            <div className="clip-trimmer-modal">
                <div className="modal-header">
                    <h3>Fine-Tune Clip</h3>
                    <button className="icon-btn" onClick={onClose}><X size={20} /></button>
                </div>

                <div className="modal-body">
                    <div className="time-display">
                        <div className="time-box">
                            <label>Start</label>
                            <span>{start.toFixed(1)}s</span>
                        </div>
                        <div className="duration-box">
                            {(end - start).toFixed(1)}s
                        </div>
                        <div className="time-box">
                            <label>End</label>
                            <span>{end.toFixed(1)}s</span>
                        </div>
                    </div>

                    {/* Timeline */}
                    <div className="trimmer-timeline-container" ref={containerRef}>
                        <div className="timeline-scale">
                            {[...Array(11)].map((_, i) => (
                                <div key={i} className="tick"></div>
                            ))}
                        </div>
                        <div className="timeline-bg"></div>
                        <div
                            className="timeline-selection"
                            style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                        >
                            <div
                                className="timeline-handle handle-left"
                                onPointerDown={handlePointerDown('start')}
                            >
                                <div className="handle-grip"></div>
                            </div>
                            <div
                                className="timeline-handle handle-right"
                                onPointerDown={handlePointerDown('end')}
                            >
                                <div className="handle-grip"></div>
                            </div>
                        </div>
                    </div>

                    <div className="trimmer-hint">
                        Visible range: {minTime.toFixed(1)}s - {maxTime.toFixed(1)}s (±10s buffer)
                    </div>

                    {/* Transcript View */}
                    <div className="trimmer-transcript">
                        <div className="transcript-label">Transcript Context</div>
                        <div className="transcript-content">
                            {loadingTranscript ? (
                                <div className="transcript-loading">Loading transcript context...</div>
                            ) : visibleWords.length > 0 ? (
                                <p>
                                    {visibleWords.map((word, idx) => {
                                        const isActive = word.start >= start && word.end <= end;
                                        // Partial overlap check logic if strict start/end is not enough?
                                        // Simple: if mid-point of word is inside selection.
                                        const mid = (word.start + word.end) / 2;
                                        const isSelected = mid >= start && mid <= end;

                                        return (
                                            <span
                                                key={idx}
                                                className={`transcript-word ${isSelected ? 'in-range' : 'out-of-range'}`}
                                                title={`${word.start.toFixed(1)}s - ${word.end.toFixed(1)}s`}
                                            >
                                                {word.word}{" "}
                                            </span>
                                        );
                                    })}
                                </p>
                            ) : (
                                <div className="transcript-empty">No transcript data available for this range.</div>
                            )}
                        </div>
                    </div>

                </div>

                <div className="modal-footer">
                    <button className="btn-secondary" onClick={() => {
                        setStart(clip.start);
                        setEnd(clip.end);
                    }}>
                        <RotateCcw size={16} /> Reset
                    </button>
                    <button className="btn-primary" onClick={handleSave}>
                        <Check size={16} /> Apply
                    </button>
                </div>
            </div>
        </div>
    );
};
